import { useState, useEffect, useRef } from 'react';
import { useFormacao, type NovaFormacaoInput } from '@/context/FormacaoContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useCategoriasFormacao } from '@/hooks/useCategoriasFormacao';

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  anoPreSelecionado?: number | null;
}

const MODALIDADES = ['Presencial', 'E-Learning', 'Misto (Blended)', 'Videoconferência'];
const ESTADOS = ['Planeada', 'Em Curso', 'Concluída', 'Cancelada'];

const EMPTY = {
  nome: '',
  entidade_formadora: '',
  categoria: '',
  modalidade: 'Presencial',
  data_inicio: '',
  data_fim: '',
  duracao_horas: 0,
  local: '',
  descricao: '',
  estado: 'Planeada',
  financiamento: '',
  observacoes: '',
  formador_interno_id: '',
};

export default function NovaFormacaoModal({ onClose, onSuccess, anoPreSelecionado }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { criarFormacao, saving, formadores } = useFormacao();
  const { categorias: CATEGORIAS } = useCategoriasFormacao();
  const [form, setForm] = useState(() => {
    if (anoPreSelecionado) {
      return {
        ...EMPTY,
        data_inicio: `${anoPreSelecionado}-01-01`,
        data_fim: `${anoPreSelecionado}-12-31`,
      };
    }
    return EMPTY;
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof EMPTY, string>>>({});
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  function set<K extends keyof typeof EMPTY>(key: K, val: (typeof EMPTY)[K]) {
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof typeof EMPTY, string>> = {};
    if (!form.nome.trim()) errs.nome = 'O título é obrigatório';
    if (!form.entidade_formadora?.trim()) errs.entidade_formadora = 'A entidade é obrigatória';
    if (!form.categoria) errs.categoria = 'Escolha uma categoria';
    if (!form.data_inicio) errs.data_inicio = 'Data de início obrigatória';
    if (!form.data_fim) errs.data_fim = 'Data de fim obrigatória';
    if (form.data_inicio && form.data_fim && form.data_fim < form.data_inicio) {
      errs.data_fim = 'A data de fim não pode ser anterior à de início';
    }
    if (!form.duracao_horas || form.duracao_horas <= 0) errs.duracao_horas = 'Insira a duração em horas';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    const payload: NovaFormacaoInput = {
      ...form,
      municipio_id: null,
      duracao_horas: Number(form.duracao_horas),
      custo: 0,
      orcamento_previsto: 0,
      entidade_formadora: form.entidade_formadora || null,
      categoria: form.categoria || null,
      local: form.local || null,
      descricao: form.descricao || null,
      financiamento: form.financiamento || null,
      observacoes: form.observacoes || null,
      data_inicio: form.data_inicio || null,
      data_fim: form.data_fim || null,
      formador_interno_id: form.formador_interno_id || null,
    };
    const resultado = await criarFormacao(payload);
    if (resultado) {
      onSuccess?.();
      onClose();
    }
  }

  const formadorSelecionado = formadores.find((f) => f.id === form.formador_interno_id);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto mx-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
              <i className="ri-book-open-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Nova Ação de Formação</h2>
              <p className="text-[11px] text-zinc-400">Preencha os dados para registar a ação</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-5">
          {/* Banner ano pré-selecionado */}
          {anoPreSelecionado && (
            <div className="flex items-center gap-2.5 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-900 flex-shrink-0">
                <i className="ri-calendar-line text-white text-sm" />
              </div>
              <div>
                <p className="text-[12px] font-bold text-zinc-800">Formação pré-preenchida para {anoPreSelecionado}</p>
                <p className="text-[11px] text-zinc-400">Criada a partir do Plano de Formação. Ajusta as datas conforme necessário.</p>
              </div>
            </div>
          )}
          {/* Título */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Título da Ação <span className="text-rose-500">*</span>
            </label>
            <input
              ref={firstRef}
              type="text"
              value={form.nome}
              onChange={(e) => set('nome', e.target.value)}
              placeholder="Ex: Excel Avançado para Gestão Pública"
              className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.nome ? 'border-rose-400' : 'border-zinc-200'}`}
            />
            {errors.nome && <p className="text-[11px] text-rose-500 mt-1">{errors.nome}</p>}
          </div>

          {/* Entidade + Categoria */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                Entidade Formadora <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={form.entidade_formadora ?? ''}
                onChange={(e) => set('entidade_formadora', e.target.value)}
                placeholder="Ex: INA, CENFIC, British Council..."
                className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.entidade_formadora ? 'border-rose-400' : 'border-zinc-200'}`}
              />
              {errors.entidade_formadora && <p className="text-[11px] text-rose-500 mt-1">{errors.entidade_formadora}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                Categoria / Área <span className="text-rose-500">*</span>
              </label>
              <select
                value={form.categoria ?? ''}
                onChange={(e) => set('categoria', e.target.value)}
                className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.categoria ? 'border-rose-400' : 'border-zinc-200'}`}
              >
                <option value="">Selecionar área...</option>
                {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.categoria && <p className="text-[11px] text-rose-500 mt-1">{errors.categoria}</p>}
            </div>
          </div>

          {/* Modalidade + Estado */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Modalidade</label>
              <select
                value={form.modalidade}
                onChange={(e) => set('modalidade', e.target.value)}
                className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
              >
                {MODALIDADES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Estado</label>
              <select
                value={form.estado}
                onChange={(e) => set('estado', e.target.value)}
                className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
              >
                {ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Formador Interno */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-star-line text-sm text-green-600" />
                </div>
                Formador Interno
                <span className="text-[10px] font-normal text-zinc-400 ml-1">opcional</span>
              </span>
            </label>
            {formadores.length === 0 ? (
              <div className="flex items-center gap-2 px-3.5 py-2.5 border border-dashed border-zinc-200 rounded-lg bg-zinc-50">
                <div className="w-4 h-4 flex items-center justify-center text-zinc-400">
                  <i className="ri-information-line text-sm" />
                </div>
                <p className="text-[12px] text-zinc-400">Nenhum formador interno registado. Adicione em <span className="font-semibold">Formadores Internos</span>.</p>
              </div>
            ) : (
              <>
                <select
                  value={form.formador_interno_id}
                  onChange={(e) => set('formador_interno_id', e.target.value)}
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
                >
                  <option value="">— Sem formador interno —</option>
                  {formadores.map((f) => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
                {formadorSelecionado && (
                  <div className="mt-1.5 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-100 rounded-lg">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-green-100 flex-shrink-0">
                      <i className="ri-user-star-line text-xs text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-green-800">{formadorSelecionado.nome}</p>
                      {formadorSelecionado.areas_formacao && formadorSelecionado.areas_formacao.length > 0 && (
                        <p className="text-[10px] text-green-600 truncate">{formadorSelecionado.areas_formacao.join(', ')}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Datas + Horas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                Data de Início <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                value={form.data_inicio ?? ''}
                onChange={(e) => set('data_inicio', e.target.value)}
                className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.data_inicio ? 'border-rose-400' : 'border-zinc-200'}`}
              />
              {errors.data_inicio && <p className="text-[11px] text-rose-500 mt-1">{errors.data_inicio}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                Data de Fim <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                value={form.data_fim ?? ''}
                onChange={(e) => set('data_fim', e.target.value)}
                className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.data_fim ? 'border-rose-400' : 'border-zinc-200'}`}
              />
              {errors.data_fim && <p className="text-[11px] text-rose-500 mt-1">{errors.data_fim}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                Duração (horas) <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={form.duracao_horas === 0 ? '' : form.duracao_horas}
                onChange={(e) => set('duracao_horas', Number(e.target.value))}
                placeholder="Ex: 14"
                className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.duracao_horas ? 'border-rose-400' : 'border-zinc-200'}`}
              />
              {errors.duracao_horas && <p className="text-[11px] text-rose-500 mt-1">{errors.duracao_horas}</p>}
            </div>
          </div>

          {/* Local */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Local</label>
            <input
              type="text"
              value={form.local ?? ''}
              onChange={(e) => set('local', e.target.value)}
              placeholder="Ex: Sala de Formação A, Online, Sede da entidade..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
            />
          </div>

          {/* Financiamento */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Financiamento</label>
            <input
              type="text"
              value={form.financiamento ?? ''}
              onChange={(e) => set('financiamento', e.target.value)}
              placeholder="Ex: POCH, Fundo Próprio, Gratuita..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Descrição</label>
            <textarea
              rows={3}
              value={form.descricao ?? ''}
              onChange={(e) => set('descricao', e.target.value)}
              maxLength={500}
              placeholder="Breve descrição dos objetivos e conteúdos..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
            />
            <p className="text-[10px] text-zinc-400 text-right mt-0.5">{(form.descricao ?? '').length}/500</p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={handleClose}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>
                  A guardar...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                  Criar Ação
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
